<template>
  <div class="product-overview" @click="toggleLine">
    <div class="product-info">
      <div class="image" data-cy="product-image">
        <BackstageImage :src="getBoxImageProxyUrl(line.productImageUrl)" :alt="line.productGroupName" />
      </div>
      <div>
        <h6 data-cy="product-name">{{ line.productGroupName }}</h6>
        <p data-cy="item-size">{{ line.productFabricSize }}</p>
        <p data-cy="item-number">{{ line.productNumber }}</p>
      </div>
    </div>
    <div v-show="collapsed" class="status" data-cy="warranty-status-alone">
      <h6>{{ $t(`viewCase.case_line_status_label`) }}</h6>
      <p>{{ $t(`viewCase.case_line_status.${line.status?.toLowerCase()}`) }}</p>
    </div>
    <div v-show="collapsed" class="customer-name" data-cy="customer-name-alone">
      <h6>{{ $t('viewCase.customer_name') }}</h6>
      <p>{{ line.customerName || '-' }}</p>
    </div>
    <div v-show="collapsed" class="defect-reason" data-cy="defect-reason-alone">
      <h6>{{ $t(`viewCase.defect_reason`) }}</h6>
      <p>{{ selectedDefectReason }}</p>
    </div>
    <BackstageIcon
      v-if="isLineDeleteEnabled"
      class="delete-line"
      name="trash"
      data-cy="delete-line-button"
      @click.self="deleteLine(line)" />
  </div>
  <VeeForm
    v-show="!collapsed"
    class="product-details"
    data-cy="product-details"
    as="div"
    ref="form"
    name="WarrantyCaseForm"
    :validate-on-mount="true"
    :validation-schema="validationSchema">
    <div class="form-container">
      <!-- Left column -->
      <div class="col">
        <BackstageInput
          :model-value="line.customerName"
          data-cy-input="customer-name"
          :name="`customerName-${line.id}`"
          :disabled="!isEditable"
          :label="$t('viewCase.customer_name')"
          type="text"
          @update:modelValue="(val) => updateLineField('customerName', val as string)">
        </BackstageInput>

        <BackstageComboboxV3
          :model-value="line.defectReason"
          :disabled="!isEditable"
          :name="`defectReason-${line.id}`"
          :label="getLabel($t(`viewCase.defect_reason_label_${line.productMainGroup?.toLowerCase()}`), true)"
          :options="defectReasonOptions"
          data-cy="defect-reason"
          options-data-cy="defect-reason-dropdown"
          @update:modelValue="(val) => updateLineField('defectReason', val)">
        </BackstageComboboxV3>

        <BackstageInputCalendar
          :name="`purchaseDate-${line.id}`"
          :model-value="line.purchaseDate"
          :label="getLabel($t('viewCase.purchase_date'), inputRequired)"
          :max-date="new Date()"
          :placeholder="$t('global.datepicker_placeholder')"
          :read-only="!isEditable"
          data-cy="purchase-date"
          @update:modelValue="(date) => updateLineField('purchaseDate', formatDateToIsoStringIgnoringTime(date))">
        </BackstageInputCalendar>
        <BackstageComboboxV3
          v-if="line.productMainGroup === ProductMainGroups.shoes"
          :model-value="line.distanceRun"
          :disabled="!isEditable"
          :name="`distanceRun-${line.id}`"
          :label="getLabel($t(`viewCase.distance_run_label_${line.productMainGroup?.toLowerCase()}`), inputRequired)"
          :options="runningDistanceOptions"
          data-cy="distance-run"
          options-data-cy="distance-run-dropdown"
          @update:modelValue="(val) => updateLineField('distanceRun', val)">
        </BackstageComboboxV3>
        <p class="mandatory-fields">
          {{ $t('viewCase.mandatory_fields') }}
        </p>
      </div>

      <!-- Middle column -->
      <div class="col">
        <!-- Note: Product code and Production code is same -->
        <BackstageComboboxV3
          :model-value="props.productionCodes[line.productCode ?? '']"
          :disabled="!isEditable"
          :name="`productCode-${line.id}`"
          :options="productionCodeOptions"
          :label="getLabel($t(`viewCase.product_code_label_${line.productMainGroup?.toLowerCase()}`), inputRequired)"
          :placeholder="$t(`viewCase.product_code_placeholder_${line.productMainGroup?.toLowerCase()}`)"
          data-cy="production-code"
          options-data-cy="production-code-dropdown"
          @update:modelValue="(val) => updateLineField('productCode', val)">
          <template #tooltip>
            <BackstageImageTooltip :image-location="tooltipImageByProduct" />
          </template>
        </BackstageComboboxV3>

        <BackstageComboboxV3
          :model-value="props.factoryCodes[line.factoryCode ?? '']"
          :disabled="!isEditable"
          :name="`factoryCode-${line.id}`"
          :label="getLabel($t(`viewCase.factory_code_label_${line.productMainGroup?.toLowerCase()}`), inputRequired)"
          :options="factoryCodeOptions"
          data-cy="factory-code"
          options-data-cy="factory-code-dropdown"
          @update:modelValue="(val) => updateLineField('factoryCode', val)">
          <template #tooltip>
            <BackstageImageTooltip :image-location="tooltipImageByProduct" />
          </template>
        </BackstageComboboxV3>

        <div class="comment-group">
          <BackstageTextarea
            data-cy="comments-field"
            :model-value="line.comments"
            :name="`comments-${line.id}`"
            :label="translatedCommentLabel"
            :class="{ disabled: !isEditable }"
            :disabled="!isEditable"
            @update:modelValue="(val) => updateLineField('comments', val as string)">
          </BackstageTextarea>
        </div>
      </div>

      <!-- Right column -->
      <div class="col">
        <div class="images-group" data-cy="image-upload">
          <div class="images-label">
            {{
              $t(
                `viewCase.images.${line.productMainGroup?.toLowerCase()}.${isImageUploadRequired ? 'validation' : 'optional'}_label`,
                {
                  minImageCount: minImageCount,
                },
              )
            }}
          </div>
          <div class="image-upload-container">
            <template v-if="isEditable">
              <!-- Large images row -->
              <div class="large-images-row">
                <CaseLineImageInput
                  v-for="[index, image] in requiredSlotImages"
                  :id="`case-line-image-input-${line.id}-${index}`"
                  :key="index"
                  :disabled="!isEditable"
                  :model="image"
                  :line="line"
                  @click="imageTouched = true"
                  :has-validation-error="imageTouched && hasImageValidationError"
                  :delete="() => deleteImage(image, index, ImageSlotType.Required)"
                  :change="(file: File) => onChangeImage(file, index)">
                </CaseLineImageInput>
              </div>

              <!-- Small images row -->
              <div class="small-images-row">
                <CaseLineImageInput
                  size="small"
                  v-for="[index, image] in optionalSlotImages"
                  :id="`case-line-image-input-${line.id}-${index}`"
                  :key="index"
                  :disabled="!isEditable"
                  :model="image"
                  :line="line"
                  @click="imageTouched = true"
                  :delete="() => deleteImage(image, index, ImageSlotType.Optional)"
                  :change="(file: File) => onChangeImage(file, index)">
                </CaseLineImageInput>
              </div>
            </template>
            <template v-else-if="line.images?.length">
              <div class="large-images-row">
                <CaseLineImageInput
                  v-for="image in line.images"
                  :key="image.imageId"
                  :disabled="!isEditable"
                  :image-metadata="{ isSubmittedImage: true }"
                  :model="image">
                </CaseLineImageInput>
              </div>
            </template>
          </div>
          <span v-if="imageTouched && hasImageValidationError" class="validation-error">
            {{ $t('viewCase.image_validation_error_message', { minImageCount }) }}
          </span>
        </div>
      </div>
    </div>
  </VeeForm>
</template>
<script lang="ts" setup>
import { Form as VeeForm } from 'vee-validate';
import { computed, onMounted, ref, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ObjectShape } from 'yup';

import apparelProductionCodeImage from '@/assets/img/apparel-accessory-production-code.png';
import shoeProductionCodeImage from '@/assets/img/shoe-production-code.png';
import CaseLineImageInput from '@/components/cases/case-line-image-input.vue';
import BackstageComboboxV3 from '@/components/common/BackstageComboboxV3.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageImage from '@/components/common/BackstageImage.vue';
import BackstageImageTooltip from '@/components/common/BackstageImageTooltip.vue';
import BackstageInput from '@/components/common/BackstageInput.vue';
import BackstageInputCalendar from '@/components/common/BackstageInputCalendar.vue';
import BackstageTextarea from '@/components/common/BackstageTextarea.vue';
import { useFormValidation } from '@/composables/form-validation';
import { useFormat } from '@/composables/format';
import { useCaseLineImages } from '@/composables/useCaseLineImages';
import { getBoxImageProxyUrl } from '@/composables/utils';
import { IMAGE_UPLOAD_REQUIRED_LEGAL_ENTITIES } from '@/constants/case.constants';
import type { CaseLine } from '@/models/case-line.model';
import { ProductMainGroups } from '@/models/product-main-group.enum';
import { useUserStore } from '@/stores/user';
import { ImageSlotType } from '@/types/case.enum';
import { type CaseLineSlotImage } from '@/types/case.interface';
import { LegalEntityEnum } from '@onrunning/backstage-models';

interface Props {
  line: CaseLine;
  factoryCodes: Record<string, string>;
  defectReasons: Record<string, string>;
  productionCodes: Record<string, string>;
  runningDistances: Record<string, string>;
  isEditable?: boolean;
  isLineDeleteEnabled?: boolean;
  caseId: string;
}
const props = withDefaults(defineProps<Props>(), { isEditable: false });

const emit = defineEmits<{
  'update-line': [line: CaseLine];
  'delete-line': [line: CaseLine];
  'form-is-valid': [isValid: boolean];
}>();

const { t } = useI18n();

const userStore = useUserStore();
const { textRequiredValidation, dateValidation } = useFormValidation();
const { formatDateToIsoStringIgnoringTime } = useFormat();
const { minImageCount, lineImageMap, initializeImages, onChangeImage, deleteImage } = useCaseLineImages(
  toRef(props, 'line'),
  toRef(props, 'caseId'),
  (updatedLine) => emit('update-line', updatedLine),
);

const form = ref<InstanceType<typeof VeeForm>>();
const collapsed = ref(true);
const imageTouched = ref(false);

const isUSLegalEntity = computed(() => {
  return userStore.selectedAccount?.legalEntity === LegalEntityEnum.ON_US;
});

const isCanadaLegalEntity = computed(() => {
  return userStore.selectedAccount?.legalEntity === LegalEntityEnum.ON_CA;
});

const inputRequired = computed(() => {
  return !isUSLegalEntity.value && !isCanadaLegalEntity.value;
});

const getLabel = (baseLabel: string, isRequired: boolean): string => {
  return isRequired ? `${t('formsValidations.fields.default_required')} ${baseLabel}` : baseLabel;
};

const validationSchema = computed(
  (): ObjectShape => ({
    // Required for all legal entities
    [`defectReason-${props.line.id}`]: textRequiredValidation('defect_reason'),

    // Required for non US/Canada legal entities
    ...(inputRequired.value && {
      [`purchaseDate-${props.line.id}`]: dateValidation('purchase_date', { required: true, maxDate: new Date() }),
      [`productCode-${props.line.id}`]: textRequiredValidation('product_code'),
      [`factoryCode-${props.line.id}`]: textRequiredValidation('factory_code'),
    }),
    ...(inputRequired.value &&
      props.line.productMainGroup === ProductMainGroups.shoes && {
        [`distanceRun-${props.line.id}`]: textRequiredValidation('distance_run'),
      }),
    ...(inputRequired.value &&
      isAdditionalDetailsRequired.value && {
        [`comments-${props.line.id}`]: textRequiredValidation('comments'),
      }),
  }),
);

const selectedDefectReason = computed((): string => {
  return props.defectReasons?.[props.line.defectReason as string] ?? props.line.defectReason ?? '-';
});

const defectReasonOptions = computed(() =>
  Object.entries(props.defectReasons).map(([value, label]) => ({ value, label })),
);

const runningDistanceOptions = computed(() => {
  return Object.entries(props.runningDistances).map(([value, label]) => ({ value, label }));
});

const productionCodeOptions = computed(() => {
  return Object.entries(props.productionCodes).map(([value, label]) => ({ value, label }));
});

const factoryCodeOptions = computed(() =>
  Object.entries(props.factoryCodes).map(([value, label]) => ({ value, label })),
);

const tooltipImageByProduct = computed(() => {
  return props.line.productMainGroup === ProductMainGroups.shoes ? shoeProductionCodeImage : apparelProductionCodeImage;
});

const translatedCommentLabel = computed((): string => {
  return isAdditionalDetailsRequired.value && inputRequired.value
    ? `${getLabel(t('viewCase.comments'), true)}`
    : t('viewCase.comments_optional');
});

const isAdditionalDetailsRequired = computed(() => {
  return !props.line.images?.length || props.line.images.length < minImageCount.value;
});

const isImageUploadRequired = computed(() => {
  const legalEntity = userStore.selectedAccount?.legalEntity as LegalEntityEnum;

  return IMAGE_UPLOAD_REQUIRED_LEGAL_ENTITIES.has(legalEntity);
});

const hasImageValidationError = computed(() => {
  // Only enforce image upload validation for certain legal entities
  if (!isImageUploadRequired.value) {
    return false;
  }

  const requiredImages = lineImageMap.value?.required.values();

  return Array.from(requiredImages).some((image) => !!image.required && !image.imageUrl);
});

const requiredSlotImages = computed(() => {
  return lineImageMap.value?.required || new Map<number, CaseLineSlotImage>();
});

const optionalSlotImages = computed(() => {
  return lineImageMap.value?.optional || new Map<number, CaseLineSlotImage>();
});

const formIsValid = computed(() => {
  return !!form.value?.meta?.valid && !hasImageValidationError.value;
});

watch(
  () => formIsValid.value,
  (isValid: boolean) => {
    emit('form-is-valid', isValid);
  },
  { immediate: true },
);

onMounted(() => {
  initializeImages();
});

const toggleLine = () => {
  collapsed.value = !collapsed.value;
};

const updateLineField = <K extends keyof CaseLine>(field: K, value: CaseLine[K]) => {
  emit('update-line', { ...props.line, [field]: value });
};

const deleteLine = (line: CaseLine) => {
  emit('delete-line', line);
};
</script>

<style scoped lang="scss">
.product-overview {
  grid-column: 1 / -1;

  display: grid;
  grid-template-columns: subgrid;

  cursor: pointer;

  .product-info {
    display: flex;
    gap: 1.25rem;

    .image {
      width: 5rem;
      height: 5rem;

      img {
        height: 100%;
        width: 100%;
        object-fit: contain;
      }
    }
  }

  h6 {
    text-align: left;
    color: var(--primary-text-color);
    font-size: 1rem;
    font-weight: 700;
    margin: 0;
  }

  p {
    text-align: left;
    font-size: 0.875rem;
    font-weight: 400;
    margin: 0;
  }

  .delete-line {
    width: 1.5rem;
    height: 1.5rem;
    margin-left: auto;

    grid-column: -1;
    grid-row: 1;
  }
}

.product-details {
  grid-column: 1 / -1;

  padding: 0.625rem 0;
  overflow: visible;
  max-height: fit-content;

  .form-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;

    .col {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .images-group {
      .image-upload-container {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 0.75rem;
      }

      .large-images-row,
      .small-images-row {
        display: grid;
        gap: 0.5rem;
        width: 100%;
      }

      .large-images-row {
        grid-template-columns: repeat(auto-fit, 150px);
      }

      .small-images-row {
        grid-template-columns: repeat(auto-fit, 70px);
      }
    }

    .validation-error {
      color: red;
      margin-top: 0.5rem;
    }

    .mandatory-fields {
      color: var(--primary-text-remark-color);
      border: none;
      font-size: small;
      text-align: left;
      background-color: transparent;
    }
  }
}

@include breakpoint(medium) {
  .product-overview {
    .product-info {
      width: 90%;
      grid-column: 1 / -1;
    }

    .defect-reason,
    .status,
    .customer-name {
      display: none;
    }
  }

  .product-details {
    .form-container {
      grid-template-columns: 1fr;

      .col {
        margin-bottom: 0.75rem;
      }
    }
  }
}
</style>
